/**
 * 計助 V1 ダッシュボード用 Zustand ストア
 *
 * 設計方針:
 * - .keisuke ファイルとして State ツリー全体をシリアライズする前提のため、
 *   すべての state は JSON 化可能なプレーンなオブジェクトにする。
 * - データグリッド変更時は 300〜500ms のデバウンスでストアを更新し、
 *   完了後に自動再計算（runCurrentAnalysis）を実行する。
 * - 長形式 gridData は表示用に最低行数まで空行パディングする（編集余白）。
 *   保存時は末尾の空行をトリムする（serializeDashboardState）。
 */

import { create } from 'zustand';
import { runTTestAdapter } from '../../../utils/adapters/tTestAdapter';
import { runAnovaAdapter } from '../../../utils/adapters/anovaAdapter';
import { runDoseResponseAdapter } from '../../../utils/adapters/doseResponseAdapter';
import { runSurvivalAdapter } from '../../../utils/adapters/survivalAdapter';
import { runBasicStatsAdapter } from '../../../utils/adapters/basicStatsAdapter';
import { runQpcrAdapter } from '../../../utils/adapters/qpcrAdapter';
import { runLongitudinalAdapter } from '../../../utils/adapters/longitudinalAdapter';
import { runRegressionAdapter } from '../../../utils/adapters/regressionAdapter';
import { kt } from '../../../utils/keisukeAdapterI18n';
import {
  normalizeGridDataForStore,
  parseFiniteNumberFromCell,
  trimTrailingEmptyGridRows,
} from '../../../utils/keisukeGridData';
import { coerceInputFormTypeToMatchAnalysis } from '../../../utils/keisukeInputFormUtils';
import { finalizeDoseResponseOptions } from '../../../utils/keisukeDoseResponseDefaults';
import { sanitizeAnalysisOptions } from '../../../utils/keisukeAnalysisOptionsSanitize';
import { getKeisukeDashboardDataGridLayout } from '../../../components/systems/keisuke/dashboard/keisukeDashboardDataGridLayout';
import {
  DEFAULT_CENTER_COLUMN_RATIO,
  DEFAULT_CENTER_DATA_RESULTS_RATIO,
  DEFAULT_LEFT_ANALYSIS_FILE_RATIO,
  DEFAULT_LEFT_COLUMN_RATIO,
  DEFAULT_RIGHT_CHART_SETTINGS_RATIO,
  sanitizeCenterColumnRatio,
  sanitizeColumnVerticalRatio,
  sanitizeLeftColumnRatio,
} from '../../../components/systems/keisuke/dashboard/keisukeDashboardLayoutStorage';

/** デスクトップ HD で空の t検定表が小さく見えない既定行数（群あたり） */
/** 空表の初期行。8だと初見カードが高くなりすぎるので 6（＋で増やせる） */
export const KEISUKE_DEFAULT_TTEST_N_PER_GROUP = 6;

/** 入力途中の検証エラー（結果帯・グラフ列を広げない） */
export function isKeisukeIncompleteAnalysisError(message: string): boolean {
  return /群のデータが必要|少なくとも\d|need (at least )?\d|at least two groups|two groups? (of data )?(are )?required|groups of data are required|insufficient data|データが不足/i.test(
    message
  );
}

/** グリッド1行の型（既存 DataRow と互換） */
export interface DataRow {
  [key: string]: number | string;
}

/** 解析タイプ（V1 ダッシュボードで選択可能） */
export const ANALYSIS_TYPE_KEYS = [
  't-test',
  'anova',
  'dose-response',
  'survival',
  'basic-stats',
  'qpcr',
  'longitudinal',
  'regression',
  'bar-chart',
] as const;

export type AnalysisTypeKey = (typeof ANALYSIS_TYPE_KEYS)[number];

export function normalizeAnalysisType(raw: unknown): AnalysisTypeKey {
  if (typeof raw === 'string' && (ANALYSIS_TYPE_KEYS as readonly string[]).includes(raw)) {
    return raw as AnalysisTypeKey;
  }
  return 't-test';
}

/** 対応なし → Mann–Whitney、対応あり → Wilcoxon */
export function ttestNonparametricInferential(
  testType: 'unpaired' | 'paired' | undefined
): 'mann-whitney' | 'wilcoxon' {
  return (testType ?? 'unpaired') === 'paired' ? 'wilcoxon' : 'mann-whitney';
}

/** データ入力形式（表のレイアウト）。ユーザーが選択 */
export type InputFormTypeKey =
  | 'longFormat'
  | 'tTestTwoColumns'
  | 'oneWayColumns'
  | 'twoWayBlocks'
  | 'doseResponseForm'
  | 'survivalForm'
  | 'repeatedForm';

/** 変数マッピング（解析タイプに応じて使う列を指定） */
export interface VariableMapping {
  groupColumn?: string;
  valueColumn?: string;
  beforeColumn?: string;
  afterColumn?: string;
  /** ANOVA: 1要因の群 / 2要因の因子A */
  factorAColumn?: string;
  /** ANOVA: 2要因の因子B */
  factorBColumn?: string;
  /** ANOVA: 反復測定の被験体ID */
  subjectColumn?: string;
  /** 用量反応: 処置量（用量・濃度） */
  doseColumn?: string;
  /** 用量反応: 反応数（死亡数・効果数） */
  effectColumn?: string;
  /** 用量反応: 総数（TCID50等で必要） */
  totalColumn?: string;
  /** 生存曲線: 時間 */
  timeColumn?: string;
  /** 生存曲線: イベント（1/0 または true/false） */
  eventColumn?: string;
  /** 生存曲線: 群 */
  survivalGroupColumn?: string;
  /**
   * 対応なしワイド2列表で group/value が群名同士になったとき、
   * 本来の測定列名（Weight 等）を軸タイトル用に残す。
   */
  valueAxisLabel?: string;
}

/** 解析設定（左ペイン・アコーディオン用） */
export interface AnalysisOptions {
  /** 解析タイプ。未設定・空文字は runCurrentAnalysis 等で t-test 扱い（旧 .keisuke 互換） */
  type?: AnalysisTypeKey | '';
  /** t検定: 対応なし / 対応あり */
  testType?: 'unpaired' | 'paired';
  /** t検定: パラメトリック / Mann–Whitney / Wilcoxon */
  ttestInferential?: 'parametric' | 'mann-whitney' | 'wilcoxon';
  /** t検定（対応なし）: 等分散を仮定するか（false = Welch） */
  equalVariance?: boolean;
  /** 列のマッピング。グリッドのヘッダー名を指定 */
  mapping?: VariableMapping;
  /** ANOVA 用（Phase 2） */
  anovaType?: 'oneWay' | 'twoWay' | 'repeatedMeasures';
  /** 反復測定1要因: 古典的反復測定ANOVA / 混合効果モデル（LMM・レガシー ANOVAView 相当） */
  anovaRepeatedMethod?: 'repeated' | 'lmm';
  /** 1要因 ANOVA: 分散分析 / Kruskal–Wallis */
  anovaOneWayMethod?: 'anova' | 'kruskal-wallis';
  /** 2要因: 独立 / 混合デザイン / LMM / 完全反復（レガシー ANOVAView 相当） */
  twoWayMethod?: 'independent' | 'mixed' | 'lmm' | 'full-repeated';
  /** 2要因・完全反復: クロスオーバー条件の確認（レガシー confirmCrossover 相当） */
  twoWayFullRepeatedConfirm?: boolean;
  postHoc?: string;
  /** Dunnett 事後検定: 対照群名（未設定時は "control" または先頭群） */
  anovaControlGroup?: string;
  /** t検定: 多重比較の参考 Bonferroni 用比較数（既定 1） */
  ttestComparisonCount?: number;
  /** 論文用タブの表示言語（既定 en） */
  paperTextLocale?: 'en' | 'ja' | 'zh-CN' | 'fr' | 'es' | 'de' | 'pt' | 'ru';
  /** 用量反応: 計算方法 */
  drMethod?: 'probit' | 'logistic' | 'reed-muench' | 'spearman-karber';
  /** 用量反応: 計算タイプ（ld50 / ec50 / ec90 / tcid50） */
  drType?: 'ld50' | 'ec50' | 'ec90' | 'tcid50';
  /** qPCR: ΔCt / ΔΔCt */
  qpcrMethod?: 'deltaCt' | 'deltaDeltaCt';
  /** 回帰: 標準 / 原点通過 */
  regressionType?: 'standard' | 'origin';
  /** 回帰: ユーザーが手動で regressionType を選んだ場合 true（自動推奨を上書きしない） */
  regressionTypeManual?: boolean;
  /** 回帰: 散布図 vs 検量線 */
  regressionMode?: 'scatter' | 'calibration';
  /** 検量線モード: 未知検体（名前・吸光度）。再計算時に濃度を推定 */
  calibrationUnknownSamples?: Array<{ name: string; absorbance: number }>;
  [key: string]: unknown;
}

/** データなし時の解析設定（既定は t検定） */
export const EMPTY_ANALYSIS_OPTIONS: AnalysisOptions = {
  type: 't-test',
  mapping: {},
  anovaType: undefined,
  postHoc: undefined,
  paperTextLocale: 'en',
};

/** 結果タブのキー */
export type ResultTabKey = 'comparison' | 'stats' | 'paper';

/** 群間比較1行（t検定の2群、または事後検定のペア） */
export type GroupComparisonRow = {
  groupA: string;
  groupB: string;
  pValue: number;
  significant: boolean;
};

/** 結果キャッシュ（計算結果のプレースホルダー。将来 Tauri IPC の戻り値で充填） */
export interface ResultCache {
  summary?: string;
  stats?: Record<string, unknown>;
  updatedAt?: number;
  /** 有意差ペア（グラフのブラケット＋*** 表示用）。leftIndex/rightIndex はバーの index、pValue で記号を決定 */
  significancePairs?: Array<{ leftIndex: number; rightIndex: number; pValue: number }>;
  /** t検定: 2群（または対応ありの前後）の比較結果を1行で表示 */
  tTestPairwiseRow?: GroupComparisonRow;
  /** ANOVA 時: 事後検定の全ペアワイズ比較（群間有意差をすべて表示用） */
  anovaPairwise?: Array<{ groupA: string; groupB: string; pValue: number; significant: boolean }>;
  /** 論文用タブ: Methods/Results のコピペ用英語テキスト */
  paperText?: string;
  /**
   * 統計詳細タブ先頭に表示する短文（計算の要点・式の参照先）。
   * rawResult とは別に .keisuke に保存する（再読込時の目安用）。
   */
  statsDetailText?: string;
  /** 用量反応: 実測点とフィット曲線（ECharts用） */
  doseResponseChart?: { observed: [number, number][]; curve?: [number, number][] };
  /** 生存曲線: 群ごとのステップポイント（ECharts用） */
  survivalChart?: { group: string; points: { time: number; survival: number }[] }[];
  /** 回帰・検量線: 散布点とフィット直線 */
  regressionChart?: {
    observed: [number, number][];
    line: [number, number][];
    /** 検量線: 推定濃度×吸光度の未知検体点 [x, y] */
    unknown?: [number, number][];
  };
  /** 検量線: 未知検体の濃度推定結果 */
  calibrationEstimates?: Array<{
    name: string;
    absorbance: number;
    concentration: number | null;
  }>;
  /** 縦断データ: 群ごとの時系列 */
  longitudinalChart?: {
    series: Array<{
      group: string;
      points: Array<{ time: number; mean: number; sd?: number }>;
    }>;
  };
  /** qPCR: 相対発現量の棒グラフ用 */
  qpcrChart?: { bars: Array<{ label: string; value: number }> };
  /**
   * 計算過程・詳細レポート用の生結果（モーダルで表示）。
   * 解析タイプに応じて t-test: TTestResult+groups, anova: ANOVAResult, survival: SurvivalAnalysisResult, dose-response: 該当結果 を格納。
   */
  rawResult?: unknown;
}

export interface KeisukeDashboardState {
  // --- レイアウト（UI状態・localStorage で永続） ---
  /** ワークスペース幅に対する左カラムの割合 */
  leftColumnRatio: number;
  /** 中央+右の残り幅のうち中央が占める割合 */
  centerColumnRatio: number;
  /** 左カラム内・上（解析設定）の高さ割合 */
  leftAnalysisFileRatio: number;
  /** 中央カラム内・上（データ表）の高さ割合 */
  centerDataResultsRatio: number;
  /** 右カラム内・上（グラフ）の高さ割合 */
  rightChartSettingsRatio: number;
  /** @deprecated 旧4ペイン右幅。シリアライズ互換のため残存 */
  rightPaneWidth: number;
  /** 右ペイン内「結果」と「グラフ」の境界（.keisuke 互換） */
  rightPaneResultRatio: number;
  /** 現在開いているファイルの IndexedDB id（未保存・新規は null） */
  currentFileId: string | null;
  /** 現在開いているファイルの表示名 */
  currentFileName: string | null;

  // --- データ・オプション（.keisuke に保存する中核） ---
  /** データグリッドの行データ（内部は常に1行1件の長形式） */
  gridData: DataRow[];
  /** 列名（ヘッダー）。gridData からも導出可能だが、空データ時の表示用に保持 */
  gridHeaders: string[];
  /** 解析オプション（ANOVA タイプ、事後検定など） */
  analysisOptions: AnalysisOptions;
  /** 入力形式: ユーザーが選択した表の見た目・入力方法 */
  inputFormType: InputFormTypeKey;
  /** 2要因ANOVAブロック形式時: 1セルあたりのデータ数（省略時はデータから推定） */
  twoWayNPerCell?: number;
  /** 2要因ANOVA: 因子Aの水準数（列数）。2〜30 */
  twoWayNLevelsA: number;
  /** 2要因ANOVA: 因子Bの水準数（行ブロック数）。2〜30 */
  twoWayNLevelsB: number;
  /** t検定2列形式: 各群（または対応ありのときはペア）あたりのデータ行数 */
  tTestNPerGroup: number;
  /** 1要因ANOVA列形式: 各群あたりのデータ行数 */
  oneWayNPerGroup: number;
  /** 1要因ANOVA列形式: 群の数（＝データ列数）。t検定2列は常に2で固定のため本値は未使用 */
  oneWayGroupCount: number;
  /** 用量反応3列形式: 希釈段・用量行数 */
  doseResponseNRows: number;
  /** 生存曲線3列形式: データ行数 */
  survivalNRows: number;
  /** 反復測定3列形式: データ行数 */
  repeatedNRows: number;

  // --- 結果表示 ---
  /** 右ペインの結果タブ: 群間比較 / 統計詳細 / 論文用 */
  activeResultTab: ResultTabKey;
  /** 計算結果のキャッシュ（保存時点の結果を表示するために保持） */
  resultCache: ResultCache | null;

  // --- 計算フィードバック（シリアライズ不要だが UI 用） ---
  /** 計算中は true。グラフを薄くする or 「計算中...」表示に利用 */
  isCalculating: boolean;
  /** 最後にストアが更新された時刻（デバウンス用） */
  lastUpdatedAt: number;
  /** 計算エラー時のメッセージ（エラーバー表示用。ユーザーが閉じたらクリア） */
  lastErrorMessage: string | null;
}

export interface KeisukeDashboardActions {
  setLeftColumnRatio: (ratio: number) => void;
  setCenterColumnRatio: (ratio: number) => void;
  setLeftAnalysisFileRatio: (ratio: number) => void;
  setCenterDataResultsRatio: (ratio: number) => void;
  setRightChartSettingsRatio: (ratio: number) => void;
  setRightPaneResultRatio: (ratio: number) => void;
  /** @deprecated 旧4ペイン用。新レイアウトでは未使用 */
  setRightPaneWidth: (w: number) => void;
  setCurrentFile: (id: string | null, fileName: string | null) => void;

  setGridData: (data: DataRow[], opts?: { runAnalysis?: boolean }) => void;
  setGridHeaders: (headers: string[]) => void;
  setAnalysisOptions: (
    options: Partial<AnalysisOptions>,
    opts?: { runAnalysis?: boolean }
  ) => void;
  setInputFormType: (type: InputFormTypeKey) => void;
  setTwoWayNPerCell: (n: number | undefined) => void;
  setTwoWayNLevelsA: (n: number) => void;
  setTwoWayNLevelsB: (n: number) => void;
  setTTestNPerGroup: (n: number) => void;
  setOneWayNPerGroup: (n: number) => void;
  setOneWayGroupCount: (n: number) => void;
  setDoseResponseNRows: (n: number) => void;
  setSurvivalNRows: (n: number) => void;
  setRepeatedNRows: (n: number) => void;

  /** プレート一括「ワークスペースへ」: grid・入力形式・解析設定を原子的に更新して再計算 */
  loadDoseResponseWorkspace: (payload: LoadDoseResponseWorkspacePayload) => void;

  setActiveResultTab: (tab: ResultTabKey) => void;
  setResultCache: (cache: ResultCache | null) => void;

  setCalculating: (value: boolean) => void;

  /**
   * デバウンス付きでグリッドデータを更新する。
   * 完了後に現在の解析タイプに応じて自動再計算（runCurrentAnalysis）を実行する。
   */
  setGridDataDebounced: (data: DataRow[], delayMs?: number) => void;

  /** 現在の gridData と analysisOptions で計算を実行し resultCache を更新。エラー時は lastErrorMessage をセット */
  runCurrentAnalysis: () => void;

  /** エラーバー用メッセージをクリア */
  setLastErrorMessage: (message: string | null) => void;

  /** .keisuke ファイルから読み込んだ JSON でストアを復元する */
  hydrateFromSerialized: (data: KeisukeDashboardSerialized) => void;
}

/** 表入力の連続更新をまとめる待ち時間（短すぎると解析が走りすぎる） */
const DEBOUNCE_MS = 280;

function shouldPadGridForVirtualScroll(state: {
  inputFormType: InputFormTypeKey;
  analysisOptions: AnalysisOptions;
}): boolean {
  const effectiveForm = coerceInputFormTypeToMatchAnalysis(state.inputFormType, {
    type: state.analysisOptions.type,
    anovaType: state.analysisOptions.anovaType,
  });
  return getKeisukeDashboardDataGridLayout(effectiveForm) === 'longStackedVirtual';
}

function normalizeStoreGridData(
  headers: string[],
  raw: DataRow[],
  padForLongFormatVirtual: boolean
): DataRow[] {
  return normalizeGridDataForStore(headers, raw, padForLongFormatVirtual);
}

/** プレート一括から用量反応ワークスペースへ載せるときのペイロード */
export interface LoadDoseResponseWorkspacePayload {
  headers: string[];
  rows: DataRow[];
  doseResponseNRows: number;
  drType: 'ld50' | 'ec50' | 'ec90' | 'tcid50';
  drMethod: NonNullable<AnalysisOptions['drMethod']>;
  mapping: VariableMapping;
}

/** t検定2列・1要因列の「1群あたりのデータ数」入力の範囲 */
export const KEISUKE_GROUP_OBSERVATION_N_MIN = 1;
/** 貼付・Enter 追記とステッパー上限を一致させる（不一致だと blur で行が黙って切れる） */
export const KEISUKE_GROUP_OBSERVATION_N_MAX = 200;

export function clampKeisukeGroupObservationCount(n: number): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v) || v < KEISUKE_GROUP_OBSERVATION_N_MIN) {
    return KEISUKE_GROUP_OBSERVATION_N_MIN;
  }
  if (v > KEISUKE_GROUP_OBSERVATION_N_MAX) return KEISUKE_GROUP_OBSERVATION_N_MAX;
  return v;
}

/** 1要因列フォームの群数（列数）。ANOVA は少なくとも2群 */
const ONE_WAY_GROUP_COUNT_MIN = 2;
const ONE_WAY_GROUP_COUNT_MAX = 30;

export function clampKeisukeOneWayGroupCount(n: number): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v) || v < ONE_WAY_GROUP_COUNT_MIN) return ONE_WAY_GROUP_COUNT_MIN;
  if (v > ONE_WAY_GROUP_COUNT_MAX) return ONE_WAY_GROUP_COUNT_MAX;
  return v;
}

const INITIAL_LONG_FORMAT_HEADERS = ['group', 'value'] as const;

function createInitialKeisukeGridData(): DataRow[] {
  return normalizeGridDataForStore([...INITIAL_LONG_FORMAT_HEADERS], [], true);
}

/** 右ペイン: 上段（結果タブ）の高さの割合。下段グラフは残りを flex:1 で占有 */
export const RIGHT_PANE_RESULTS_SECTION_RATIO_DEFAULT = 0.4;
export const RIGHT_PANE_RESULTS_SECTION_RATIO_MIN = 0.15;
/** これ以上はグラフが狭くなりすぎる（右ペイン下半の図がほぼ見えない）ため上限を抑える */
export const RIGHT_PANE_RESULTS_SECTION_RATIO_MAX = 0.5;

/**
 * graphSettingsRatio = 結果タブエリアの縦の割合。グラフは常に親の残り高さを取る前提で [min,max] を制限する。
 */
export function sanitizeGraphSettingsRatio(value: unknown): number {
  const min = RIGHT_PANE_RESULTS_SECTION_RATIO_MIN;
  const max = RIGHT_PANE_RESULTS_SECTION_RATIO_MAX;
  const fb = RIGHT_PANE_RESULTS_SECTION_RATIO_DEFAULT;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return Math.max(min, Math.min(max, fb));
  return Math.max(min, Math.min(max, n));
}

const initialState: KeisukeDashboardState = {
  leftColumnRatio: DEFAULT_LEFT_COLUMN_RATIO,
  centerColumnRatio: DEFAULT_CENTER_COLUMN_RATIO,
  leftAnalysisFileRatio: DEFAULT_LEFT_ANALYSIS_FILE_RATIO,
  centerDataResultsRatio: DEFAULT_CENTER_DATA_RESULTS_RATIO,
  rightChartSettingsRatio: DEFAULT_RIGHT_CHART_SETTINGS_RATIO,
  rightPaneWidth: 420,
  rightPaneResultRatio: 0.35,
  currentFileId: null,
  currentFileName: null,
  gridData: createInitialKeisukeGridData(),
  gridHeaders: [...INITIAL_LONG_FORMAT_HEADERS],
  analysisOptions: {
    type: 't-test',
    testType: 'unpaired',
    equalVariance: false,
    mapping: { groupColumn: 'group', valueColumn: 'value' },
  },
  inputFormType: 'tTestTwoColumns',
  twoWayNPerCell: undefined,
  twoWayNLevelsA: 2,
  twoWayNLevelsB: 2,
  tTestNPerGroup: KEISUKE_DEFAULT_TTEST_N_PER_GROUP,
  oneWayNPerGroup: 3,
  oneWayGroupCount: 3,
  doseResponseNRows: 8,
  survivalNRows: 12,
  repeatedNRows: 12,
  activeResultTab: 'comparison',
  resultCache: null,
  isCalculating: false,
  lastUpdatedAt: 0,
  lastErrorMessage: null,
};

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let gridDataRunTimer: ReturnType<typeof setTimeout> | null = null;

function clearGridDataRunTimer(): void {
  if (gridDataRunTimer != null) {
    clearTimeout(gridDataRunTimer);
    gridDataRunTimer = null;
  }
}

/**
 * 縦分割比（中央ペインのデータ表 vs 下段など）を [0.2, 0.8] に収める。NaN / 非数はフォールバック。
 * localStorage やリサイズで不正値が入ると flex が `NaN%` になりグラフ領域が消えるため公開する。
 */
export function sanitizeDashboardSplitRatio(value: unknown, fallback = 0.5): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return Math.max(0.2, Math.min(0.8, fallback));
  return Math.max(0.2, Math.min(0.8, n));
}

export const useKeisukeDashboardStore = create<KeisukeDashboardState & KeisukeDashboardActions>((set, get) => ({
  ...initialState,

  setLeftColumnRatio: (ratio) => set({ leftColumnRatio: sanitizeLeftColumnRatio(ratio) }),
  setCenterColumnRatio: (ratio) => set({ centerColumnRatio: sanitizeCenterColumnRatio(ratio) }),
  setLeftAnalysisFileRatio: (ratio) =>
    set({ leftAnalysisFileRatio: sanitizeColumnVerticalRatio(ratio, DEFAULT_LEFT_ANALYSIS_FILE_RATIO) }),
  setCenterDataResultsRatio: (ratio) =>
    set({ centerDataResultsRatio: sanitizeColumnVerticalRatio(ratio, DEFAULT_CENTER_DATA_RESULTS_RATIO) }),
  setRightChartSettingsRatio: (ratio) =>
    set({ rightChartSettingsRatio: sanitizeColumnVerticalRatio(ratio, DEFAULT_RIGHT_CHART_SETTINGS_RATIO) }),
  setRightPaneResultRatio: (ratio) => set({ rightPaneResultRatio: sanitizeDashboardSplitRatio(ratio, 0.35) }),
  setRightPaneWidth: (w) =>
    set({ rightPaneWidth: Math.max(320, Math.min(800, w)) }),
  setCurrentFile: (id, fileName) => set({ currentFileId: id, currentFileName: fileName ?? null }),

  setGridData: (data, opts) => {
    const runAnalysis = opts?.runAnalysis !== false;
    clearGridDataRunTimer();
    const raw = Array.isArray(data) ? data : [];
    const isEmptyInput = raw.length === 0;
    const headers = get().gridHeaders;
    const gridData = normalizeStoreGridData(
      headers,
      raw,
      shouldPadGridForVirtualScroll(get())
    );
    set((s) => ({
      gridData,
      lastUpdatedAt: Date.now(),
      isCalculating: runAnalysis && !isEmptyInput,
      // 空データ時は結果のみクリアし、解析タイプは維持する（2要因ANOVA切替直後の空フォームで type が '' になり点滅する不具合を防止）
      ...(isEmptyInput ? { resultCache: null } : {}),
    }));
    if (!runAnalysis) return;
    gridDataRunTimer = setTimeout(() => {
      gridDataRunTimer = null;
      set({ isCalculating: false });
      if (!isEmptyInput) get().runCurrentAnalysis();
    }, 500);
  },
  setGridHeaders: (headers) => set({ gridHeaders: headers }),
  setInputFormType: (type) =>
    set((s) => {
      const analysisOptions = s.analysisOptions;
      const oldForm = coerceInputFormTypeToMatchAnalysis(s.inputFormType, {
        type: analysisOptions.type,
        anovaType: analysisOptions.anovaType,
      });
      const newForm = coerceInputFormTypeToMatchAnalysis(type, {
        type: analysisOptions.type,
        anovaType: analysisOptions.anovaType,
      });
      const oldPad = getKeisukeDashboardDataGridLayout(oldForm) === 'longStackedVirtual';
      const newPad = getKeisukeDashboardDataGridLayout(newForm) === 'longStackedVirtual';
      if (oldPad === newPad) return { inputFormType: type };
      return {
        inputFormType: type,
        gridData: normalizeStoreGridData(s.gridHeaders, s.gridData, newPad),
      };
    }),
  setTwoWayNPerCell: (n) => set({ twoWayNPerCell: n }),
  setTwoWayNLevelsA: (n) => set({ twoWayNLevelsA: clampKeisukeOneWayGroupCount(n) }),
  setTwoWayNLevelsB: (n) => set({ twoWayNLevelsB: clampKeisukeOneWayGroupCount(n) }),
  setTTestNPerGroup: (n) => set({ tTestNPerGroup: clampKeisukeGroupObservationCount(n) }),
  setOneWayNPerGroup: (n) => set({ oneWayNPerGroup: clampKeisukeGroupObservationCount(n) }),
  setOneWayGroupCount: (n) => set({ oneWayGroupCount: clampKeisukeOneWayGroupCount(n) }),
  setDoseResponseNRows: (n) => set({ doseResponseNRows: clampKeisukeGroupObservationCount(n) }),
  setSurvivalNRows: (n) => set({ survivalNRows: clampKeisukeGroupObservationCount(n) }),
  setRepeatedNRows: (n) => set({ repeatedNRows: clampKeisukeGroupObservationCount(n) }),

  loadDoseResponseWorkspace: (payload) => {
    clearGridDataRunTimer();
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    const headers = [...payload.headers];
    const gridData = normalizeStoreGridData(headers, payload.rows, false);
    const analysisOptions = finalizeDoseResponseOptions({
      ...get().analysisOptions,
      type: 'dose-response',
      drType: payload.drType,
      drMethod: payload.drMethod,
      mapping: { ...payload.mapping },
    });
    set({
      gridHeaders: headers,
      gridData,
      inputFormType: 'doseResponseForm',
      doseResponseNRows: clampKeisukeGroupObservationCount(payload.doseResponseNRows),
      analysisOptions,
      isCalculating: false,
      lastErrorMessage: null,
      lastUpdatedAt: Date.now(),
    });
    get().runCurrentAnalysis();
  },

  setAnalysisOptions: (opts, runOpts) => {
    const mergeOptions = (prev: AnalysisOptions, patch: Partial<AnalysisOptions>): AnalysisOptions => {
      let analysisOptions = { ...prev, ...patch, type: normalizeAnalysisType(patch.type ?? prev.type) };
      if (analysisOptions.type === 'dose-response') {
        analysisOptions = finalizeDoseResponseOptions(analysisOptions);
      }
      /** 対応なし⇔あり切替時、非パラメトリック手法名を揃える（mann-whitney ↔ wilcoxon） */
      if (analysisOptions.type === 't-test') {
        const inf = analysisOptions.ttestInferential ?? 'parametric';
        if (inf !== 'parametric') {
          const expected = ttestNonparametricInferential(analysisOptions.testType);
          if (inf !== expected) {
            analysisOptions = { ...analysisOptions, ttestInferential: expected };
          }
        }
      }
      return analysisOptions;
    };
    if (runOpts?.runAnalysis === false) {
      set((s) => {
        const analysisOptions = mergeOptions(s.analysisOptions, opts);
        const inputFormType = coerceInputFormTypeToMatchAnalysis(s.inputFormType, {
          type: analysisOptions.type,
          anovaType: analysisOptions.anovaType,
        });
        const oldPad = shouldPadGridForVirtualScroll(s);
        const newPad = shouldPadGridForVirtualScroll({ ...s, inputFormType, analysisOptions });
        const gridData =
          oldPad !== newPad
            ? normalizeStoreGridData(s.gridHeaders, s.gridData, newPad)
            : s.gridData;
        return { analysisOptions, inputFormType, gridData };
      });
      return;
    }
    set((s) => {
      const analysisOptions = mergeOptions(s.analysisOptions, opts);
      const inputFormType = coerceInputFormTypeToMatchAnalysis(s.inputFormType, {
        type: analysisOptions.type,
        anovaType: analysisOptions.anovaType,
      });
      const oldPad = shouldPadGridForVirtualScroll(s);
      const newPad = shouldPadGridForVirtualScroll({ ...s, inputFormType, analysisOptions });
      const gridData =
        oldPad !== newPad
          ? normalizeStoreGridData(s.gridHeaders, s.gridData, newPad)
          : s.gridData;
      /** 解析種別が変わったら旧 rawResult を残さない（論文テキスト等が型不一致で落ちる） */
      const typeChanged =
        normalizeAnalysisType(s.analysisOptions.type) !== analysisOptions.type;
      return {
        analysisOptions,
        inputFormType,
        gridData,
        isCalculating: true,
        ...(typeChanged ? { resultCache: null, lastErrorMessage: null } : {}),
      };
    });
    setTimeout(() => {
      set({ isCalculating: false });
      get().runCurrentAnalysis();
    }, 600);
  },

  setActiveResultTab: (tab) => set({ activeResultTab: tab }),
  setResultCache: (cache) => set({ resultCache: cache }),
  setLastErrorMessage: (message) => set({ lastErrorMessage: message }),

  setCalculating: (value) => set({ isCalculating: value }),

  runCurrentAnalysis: () => {
    const state = get();
    const { gridData: data, analysisOptions: options, gridHeaders } = state;
    const type = normalizeAnalysisType(options.type);
    if (!Array.isArray(data) || data.length === 0) {
      set({ resultCache: null, lastErrorMessage: null });
      return;
    }
    const mapping = options.mapping ?? {};
    const valueCol = mapping.valueColumn;
    const headers = Array.isArray(gridHeaders) ? gridHeaders : [];
    let hasAnyNumeric = false;
    if (valueCol && headers.includes(valueCol)) {
      hasAnyNumeric = data.some((row) => parseFiniteNumberFromCell(row[valueCol]) !== null);
    }
    if (!hasAnyNumeric && headers.length > 0) {
      hasAnyNumeric = data.some((row) =>
        headers.some((h) => parseFiniteNumberFromCell(row[h]) !== null)
      );
    }
    if (!hasAnyNumeric) {
      hasAnyNumeric = data.some((row) =>
        Object.values(row).some((v) => parseFiniteNumberFromCell(v) !== null)
      );
    }
    if (!hasAnyNumeric) {
      set({ resultCache: null, lastErrorMessage: null });
      return;
    }
    try {
      set({ lastErrorMessage: null });
      let cache: ResultCache;
      if (type === 'anova') cache = runAnovaAdapter(data, options);
      else if (type === 'dose-response') cache = runDoseResponseAdapter(data, options);
      else if (type === 'survival') cache = runSurvivalAdapter(data, options);
      else if (type === 'basic-stats') cache = runBasicStatsAdapter(data, options, gridHeaders);
      else if (type === 'qpcr') cache = runQpcrAdapter(data, options, gridHeaders);
      else if (type === 'longitudinal') cache = runLongitudinalAdapter(data, options);
      else if (type === 'regression') cache = runRegressionAdapter(data, options);
      else if (type === 'bar-chart') {
        cache = {
          summary: kt('summary.barChartMode'),
          stats: { mode: 'bar-chart' },
          updatedAt: Date.now(),
        };
      } else cache = runTTestAdapter(data, options);
      set({ resultCache: cache });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set({
        resultCache: null,
        lastErrorMessage: isKeisukeIncompleteAnalysisError(message) ? null : message,
      });
    }
  },

  setGridDataDebounced: (data, delayMs = DEBOUNCE_MS) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const raw = Array.isArray(data) ? data : [];
    const isEmptyInput = raw.length === 0;
    // Defer all store updates to next frame so we never run during React commit/flush (avoids recoverFromConcurrentError)
    const schedule = (fn: () => void) => {
      requestAnimationFrame(() => {
        fn();
      });
    };
    schedule(() => set({ isCalculating: true }));
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      schedule(() => {
        const headers = get().gridHeaders;
        const gridData = normalizeStoreGridData(
          headers,
          raw,
          shouldPadGridForVirtualScroll(get())
        );
        set((s) => ({
          gridData,
          lastUpdatedAt: Date.now(),
          isCalculating: false,
          ...(isEmptyInput ? { resultCache: null } : {}),
        }));
        if (!isEmptyInput) get().runCurrentAnalysis();
      });
    }, delayMs);
  },

  hydrateFromSerialized: (data) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    clearGridDataRunTimer();
    const validTabs: ResultTabKey[] = ['comparison', 'stats', 'paper'];
    const rawTab = data.activeResultTab as string | undefined;
    const migratedTab =
      rawTab === 'easy' || rawTab === 'meaning' ? 'comparison' : rawTab;
    const activeResultTab =
      migratedTab && validTabs.includes(migratedTab as ResultTabKey)
        ? (migratedTab as ResultTabKey)
        : 'comparison';
    const rawGrid = Array.isArray(data.gridData) ? data.gridData : initialState.gridData;
    const gridHeaders = Array.isArray(data.gridHeaders) ? data.gridHeaders : initialState.gridHeaders;
    const isEmpty = rawGrid.length === 0;
    /** 空データでも解析タイプは維持（新規で basic-stats 等が t検定に戻らない） */
    const restoredOptions = (() => {
      if (isEmpty) {
        return {
          ...EMPTY_ANALYSIS_OPTIONS,
          type: normalizeAnalysisType(data.analysisOptions?.type ?? EMPTY_ANALYSIS_OPTIONS.type),
          paperTextLocale:
            typeof data.analysisOptions?.paperTextLocale === 'string'
              ? (data.analysisOptions.paperTextLocale as AnalysisOptions['paperTextLocale'])
              : EMPTY_ANALYSIS_OPTIONS.paperTextLocale,
        };
      }
      let options = sanitizeAnalysisOptions(data.analysisOptions, EMPTY_ANALYSIS_OPTIONS);
      if (options.type === 'dose-response') {
        options = finalizeDoseResponseOptions(options);
      }
      return options;
    })();
    const coercedInputFormType = coerceInputFormTypeToMatchAnalysis(
      data.inputFormType ?? 'tTestTwoColumns',
      { type: restoredOptions.type, anovaType: restoredOptions.anovaType }
    );
    const padForVirtual = getKeisukeDashboardDataGridLayout(coercedInputFormType) === 'longStackedVirtual';
    const gridData = normalizeGridDataForStore(gridHeaders, rawGrid, padForVirtual);
    set({
      rightPaneResultRatio: sanitizeDashboardSplitRatio(data.rightPaneResultRatio, 0.35),
      gridData,
      gridHeaders,
      analysisOptions: restoredOptions,
      inputFormType: coercedInputFormType,
      twoWayNPerCell: data.twoWayNPerCell,
      twoWayNLevelsA: clampKeisukeOneWayGroupCount(
        typeof data.twoWayNLevelsA === 'number' ? data.twoWayNLevelsA : 2
      ),
      twoWayNLevelsB: clampKeisukeOneWayGroupCount(
        typeof data.twoWayNLevelsB === 'number' ? data.twoWayNLevelsB : 2
      ),
      tTestNPerGroup: clampKeisukeGroupObservationCount(
        typeof data.tTestNPerGroup === 'number'
          ? data.tTestNPerGroup
          : KEISUKE_DEFAULT_TTEST_N_PER_GROUP
      ),
      oneWayNPerGroup: clampKeisukeGroupObservationCount(
        typeof data.oneWayNPerGroup === 'number' ? data.oneWayNPerGroup : 3
      ),
      oneWayGroupCount: clampKeisukeOneWayGroupCount(
        typeof data.oneWayGroupCount === 'number' ? data.oneWayGroupCount : 3
      ),
      doseResponseNRows: clampKeisukeGroupObservationCount(
        typeof data.doseResponseNRows === 'number' ? data.doseResponseNRows : 8
      ),
      survivalNRows: clampKeisukeGroupObservationCount(
        typeof data.survivalNRows === 'number' ? data.survivalNRows : 12
      ),
      repeatedNRows: clampKeisukeGroupObservationCount(
        typeof data.repeatedNRows === 'number' ? data.repeatedNRows : 12
      ),
      activeResultTab,
      resultCache: isEmpty ? null : (data.resultCache ?? null),
    });
    if (!isEmpty) {
      get().runCurrentAnalysis();
    }
  },
}));

/** 左カラムと中央カラムの境界 */
export function applyLeftCenterColumnSplitFromViewportX(clientX: number): void {
  const rowEl = document.querySelector<HTMLElement>('[data-keisuke-columns-row]');
  const row = rowEl?.getBoundingClientRect();
  if (!row || row.width <= 0) return;
  const ratio = (clientX - row.left) / row.width;
  useKeisukeDashboardStore.getState().setLeftColumnRatio(ratio);
}

/** 中央カラムと右カラムの境界 */
export function applyCenterRightColumnSplitFromViewportX(clientX: number): void {
  const rowEl = document.querySelector<HTMLElement>('[data-keisuke-columns-row]');
  const row = rowEl?.getBoundingClientRect();
  if (!row || row.width <= 0) return;
  const st = useKeisukeDashboardStore.getState();
  const remainderLeft = row.left + row.width * st.leftColumnRatio;
  const remainderWidth = row.width * (1 - st.leftColumnRatio);
  if (remainderWidth <= 0) return;
  const centerRatio = (clientX - remainderLeft) / remainderWidth;
  st.setCenterColumnRatio(centerRatio);
}

export function applyLeftVerticalSplitFromViewportY(clientY: number): void {
  const colEl = document.querySelector<HTMLElement>('[data-keisuke-left-column]');
  const col = colEl?.getBoundingClientRect();
  if (!col || col.height <= 0) return;
  const ratio = (clientY - col.top) / col.height;
  useKeisukeDashboardStore.getState().setLeftAnalysisFileRatio(ratio);
}

export function applyCenterVerticalSplitFromViewportY(clientY: number): void {
  const colEl = document.querySelector<HTMLElement>('[data-keisuke-center-column]');
  const col = colEl?.getBoundingClientRect();
  if (!col || col.height <= 0) return;
  const ratio = (clientY - col.top) / col.height;
  useKeisukeDashboardStore.getState().setCenterDataResultsRatio(ratio);
}

export function applyRightVerticalSplitFromViewportY(clientY: number): void {
  const colEl = document.querySelector<HTMLElement>('[data-keisuke-right-column]');
  const col = colEl?.getBoundingClientRect();
  if (!col || col.height <= 0) return;
  const ratio = (clientY - col.top) / col.height;
  useKeisukeDashboardStore.getState().setRightChartSettingsRatio(ratio);
}

/** @deprecated 旧レイアウト互換 */
export function applyResultsStripSplitFromViewportY(clientY: number): void {
  applyCenterVerticalSplitFromViewportY(clientY);
}

/** @deprecated 旧レイアウト互換 */
export function applyUnifiedVerticalSplitFromViewportY(clientY: number): void {
  applyCenterVerticalSplitFromViewportY(clientY);
}

/** @deprecated 旧レイアウト互換 */
export function applyDataChartSplitFromViewportX(clientX: number): void {
  applyLeftCenterColumnSplitFromViewportX(clientX);
}

/** .keisuke ファイルのフォーマットバージョン（互換性・将来のマイグレーション用） */
export const KEISUKE_FILE_VERSION = 1;

/** .keisuke に保存するペイロードの型（レイアウト等は含めず、復元時に既定値を使用） */
export type KeisukeDashboardSerialized = {
  /** ファイル形式バージョン（省略時は 1 扱い） */
  version?: number;
  /** 保存日時（ISO 文字列、表示用） */
  savedAt?: string;
  rightPaneResultRatio?: number;
  gridData: DataRow[];
  gridHeaders: string[];
  analysisOptions: AnalysisOptions;
  inputFormType?: InputFormTypeKey;
  twoWayNPerCell?: number;
  twoWayNLevelsA?: number;
  twoWayNLevelsB?: number;
  tTestNPerGroup?: number;
  oneWayNPerGroup?: number;
  oneWayGroupCount?: number;
  doseResponseNRows?: number;
  survivalNRows?: number;
  repeatedNRows?: number;
  activeResultTab?: ResultTabKey;
  resultCache: ResultCache | null;
};

/** シリアライズ可能なキーのみを取り出し、.keisuke 用のメタデータを付与。rawResult は容量が大きいため保存しない（再計算で復元可能）。 */
export function serializeDashboardState(state: KeisukeDashboardState): KeisukeDashboardSerialized {
  const resultCache = state.resultCache
    ? (() => {
        const { rawResult: _, ...rest } = state.resultCache!;
        return rest as ResultCache;
      })()
    : null;
  return {
    version: KEISUKE_FILE_VERSION,
    savedAt: new Date().toISOString(),
    rightPaneResultRatio: state.rightPaneResultRatio,
    gridData: trimTrailingEmptyGridRows(state.gridHeaders, state.gridData),
    gridHeaders: state.gridHeaders,
    analysisOptions: state.analysisOptions,
    inputFormType: state.inputFormType,
    twoWayNPerCell: state.twoWayNPerCell,
    twoWayNLevelsA: state.twoWayNLevelsA,
    twoWayNLevelsB: state.twoWayNLevelsB,
    tTestNPerGroup: state.tTestNPerGroup,
    oneWayNPerGroup: state.oneWayNPerGroup,
    oneWayGroupCount: state.oneWayGroupCount,
    doseResponseNRows: state.doseResponseNRows,
    survivalNRows: state.survivalNRows,
    repeatedNRows: state.repeatedNRows,
    activeResultTab: state.activeResultTab,
    resultCache,
  };
}

export type KeisukeFileValidationResult =
  | { ok: true; data: KeisukeDashboardSerialized }
  | { ok: false; errorKey: string };

/** 読み込み上限（DoS 対策） */
export const KEISUKE_FILE_MAX_GRID_ROWS = 20_000;
export const KEISUKE_FILE_MAX_GRID_HEADERS = 256;
export const KEISUKE_FILE_MAX_CELL_CHARS = 10_000;

const DANGEROUS_JSON_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function hasDangerousJsonKeys(value: unknown, depth = 0): boolean {
  if (depth > 32) return true;
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) {
    return value.some((item) => hasDangerousJsonKeys(item, depth + 1));
  }
  for (const key of Object.keys(value as Record<string, unknown>)) {
    if (DANGEROUS_JSON_KEYS.has(key)) return true;
    if (hasDangerousJsonKeys((value as Record<string, unknown>)[key], depth + 1)) return true;
  }
  return false;
}

function gridWithinLimits(gridData: unknown[], gridHeaders: unknown[]): boolean {
  if (gridData.length > KEISUKE_FILE_MAX_GRID_ROWS) return false;
  if (gridHeaders.length > KEISUKE_FILE_MAX_GRID_HEADERS) return false;
  for (const row of gridData) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    for (const val of Object.values(row as Record<string, unknown>)) {
      if (typeof val === 'string' && val.length > KEISUKE_FILE_MAX_CELL_CHARS) return false;
    }
  }
  for (const h of gridHeaders) {
    if (typeof h !== 'string' || h.length > 200) return false;
  }
  return true;
}

/**
 * 読み込んだ JSON が有効な .keisuke 形式か検証する
 * 表示用メッセージは errorKey を keisuke.dashboard.* で翻訳して表示すること
 */
export function validateKeisukeFile(data: unknown): KeisukeFileValidationResult {
  if (!data || typeof data !== 'object') return { ok: false, errorKey: 'errorInvalidKeisuke' };
  if (hasDangerousJsonKeys(data)) return { ok: false, errorKey: 'errorInvalidKeisuke' };
  const o = data as Record<string, unknown>;
  if (!Array.isArray(o.gridData)) return { ok: false, errorKey: 'errorInvalidKeisuke' };
  if (!Array.isArray(o.gridHeaders)) return { ok: false, errorKey: 'errorInvalidKeisuke' };
  if (!gridWithinLimits(o.gridData, o.gridHeaders)) {
    return { ok: false, errorKey: 'errorInvalidKeisuke' };
  }
  const ver = o.version;
  if (ver !== undefined && typeof ver === 'number' && ver > KEISUKE_FILE_VERSION) {
    return { ok: false, errorKey: 'errorNewerVersion' };
  }
  return { ok: true, data: data as KeisukeDashboardSerialized };
}
